import React, { useEffect, useMemo, useState } from "react";
import { View, Text, TextInput, StyleSheet } from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { ProductFieldInfoVisibility } from "../../../../../view-models/ProductFieldInfoVisibility";

interface AdditionalReceiverInformationStepProps {
  onValidationChange?: (valid: boolean) => void;
}

const AdditionalReceiverInformationStep: React.FC<AdditionalReceiverInformationStepProps> = ({ onValidationChange }) => {
  const missingRequiredFields = useSessionStore((s: SessionState) => (s as any).missingRequiredFields) as any[];
  const setAdditionalFieldValue = useSessionStore((s: SessionState) => (s as any).setAdditionalFieldValue);
  const additionalFieldValues = useSessionStore((s: SessionState) => (s as any).additionalFieldValues) as Record<string, any>;

  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  const requiredFields = useMemo(() => (missingRequiredFields || []).filter((f: any) => (f?.visibility === ProductFieldInfoVisibility.REQ)), [missingRequiredFields]);

  useEffect(() => {
    // validate all fields to determine continue button state
    const errs: Record<string, string> = {};
    for (const f of requiredFields) {
      const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
      const label = String(f?.FieldLabel ?? f?.fieldLabel ?? key);
      const min = Number(f?.FieldMin ?? f?.fieldMin ?? 0);
      const max = Number(f?.FieldMax ?? f?.fieldMax ?? Number.MAX_SAFE_INTEGER);
      const valueRaw = additionalFieldValues?.[key] ?? "";
      const value = String(valueRaw ?? "");
      if (value.length < min) errs[key] = `${label} must be at least ${min} characters`;
      else if (value.length > max) errs[key] = `${label} must be at most ${max} characters`;
      else if (!value) errs[key] = `${label} is required`;
    }
    setLocalErrors(errs);
    onValidationChange?.(Object.keys(errs).length === 0);
  }, [requiredFields, additionalFieldValues]);

  const handleChange = (key: string, text: string) => {
    setAdditionalFieldValue?.(key, text);
  };

  if (!requiredFields?.length) {
    return (
      <View style={{ paddingHorizontal: 24 }}>
        <Text>No additional information required.</Text>
      </View>
    );
  }

  return (
    <View style={{ paddingHorizontal: 24 }}>
      <Text style={styles.headerTitle}>Provide the following information</Text>
      {requiredFields.map((f: any) => {
        const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
        const label = String(f?.FieldLabel ?? f?.fieldLabel ?? key);
        const placeholder = label;
        const value = String((additionalFieldValues?.[key] ?? ""));
        const error = localErrors[key];
        return (
          <View key={key} style={styles.fieldWrapper}>
            <Text style={styles.label}>{label}</Text>
            <TextInput
              testID={`additionalField_${key}`}
              style={[styles.input, !!error && styles.inputError]}
              placeholder={placeholder}
              value={value}
              onChangeText={(t) => handleChange(key, t)}
            />
            {!!error && <Text style={styles.error}>{error}</Text>}
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  fieldWrapper: {
    marginBottom: 16
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "700",
    marginTop: 8,
    marginBottom: 16,
    color: "#111"
  },
  label: {
    fontSize: 14,
    color: "#111",
    marginBottom: 6
  },
  input: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    backgroundColor: "#fff"
  },
  inputError: {
    borderColor: "#E11D48"
  },
  error: {
    color: "#E11D48",
    marginTop: 6,
    fontSize: 12
  }
});

export default AdditionalReceiverInformationStep;


